import { Link, useLocation } from 'react-router-dom';
import { Footer } from "@/components/Footer";
import { ArrowRight, Shield, Clock, Check, Star, X, ChartLine, Heart, Brain, Link as LinkIcon, ShieldAlert, BarChart3 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import React, { useEffect } from "react";
import { WaitlistForm } from '@/components/WaitlistForm';
import { SimpleWaitlistForm } from '@/components/SimpleWaitlistForm';
import { TextShimmer } from '@/components/ui/text-shimmer';
import { LazyImage } from '@/components/ui/lazy-image';
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useGoogleOAuth } from "@/hooks/use-google-oauth";
import { LLMOptimizedStructuredData } from "@/components/seo/LLMOptimizedStructuredData";
import { LLMContentExtraction } from "@/components/seo/LLMContentExtraction";
import { BFCMBanner } from "@/components/campaign/BFCMBanner";
import { Helmet } from "react-helmet-async";
// Static imports instead of lazy loading (code splitting disabled)
import { EnergyDashboardPreview } from "@/components/landing/EnergyDashboardPreview";
import BookingFlowDemo from "@/components/landing/BookingFlowDemo";
import { FeaturesSectionWithHoverEffects } from "@/components/landing/FeaturesSectionWithHoverEffects";

const BiostrapLogo = () => <img src="/assets/icons/wearables/biostrap-logo.svg" alt="Biostrap" className="h-6 sm:h-7 w-auto opacity-80 hover:opacity-100 transition-opacity" />;
const OuraLogo = () => <img src="/assets/icons/wearables/oura-logo.svg" alt="Oura" className="h-5 sm:h-5 w-auto opacity-80 hover:opacity-100 transition-opacity" />;
const WhoopLogo = () => <img src="/assets/icons/wearables/whoop-logo.svg" alt="Whoop" className="h-4 sm:h-4 w-auto opacity-80 hover:opacity-100 transition-opacity" />;
const UltrahumanLogo = () => <img src="/assets/icons/wearables/ultrahuman-logo.svg" alt="Ultrahuman" className="h-3 sm:h-3 w-auto opacity-80 hover:opacity-100 transition-opacity" />;

// Google Logo component with variant support
const GoogleLogo = ({ variant = 'default', className = "w-6 h-6 mr-3" }: { variant?: 'default' | 'white' | 'black', className?: string }) => {
  const getLogoSrc = () => {
    switch (variant) {
      case 'white':
        return "/assets/images/ui/google-g-logo-white.svg";
      case 'black':
        return "/assets/images/ui/google-g-logo-black.svg";
      default:
        return "/assets/images/ui/google-g-logo.svg";
    }
  };

  return (
    <img 
      src={getLogoSrc()} 
      alt="Google" 
      className={className}
    />
  );
};

const Index = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signInWithGoogle, loading: googleLoading } = useGoogleOAuth({
    customToast: toast
  });
  
  // State to control Google logo variant
  const [googleLogoVariant, setGoogleLogoVariant] = React.useState<'default' | 'white' | 'black'>('default');

  const handleGoogleSignUp = async () => {
    await signInWithGoogle(true); // true indicates this is a sign-up attempt
  };

  // Function to cycle through logo variants (for demonstration)
  const cycleLogoVariant = () => {
    setGoogleLogoVariant(prev => {
      switch (prev) {
        case 'default': return 'white';
        case 'white': return 'black';
        case 'black': return 'default';
        default: return 'default';
      }
    });
  };

  useEffect(() => {
    // Check if we should scroll to waitlist from navigation state
    if (location.state?.scrollToWaitlist) {
      const waitlistElement = document.getElementById('waitlist');
      if (waitlistElement) {
        setTimeout(() => {
          waitlistElement.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.state]);

  return <div>
      <Helmet>
        <title>ChronoCal - The Performance Calendar That Works With Your Biology</title>
        <meta name="description" content="ChronoCal is the world's first performance-based calendar that schedules your work when your brain is wired to win. Integrate your wearables, optimize your energy, and reclaim 15+ hours of peak productivity per week." />
        <meta name="keywords" content="calendar app, productivity software, energy management, wearable integration, chronobiology, circadian rhythm, deep work, meeting scheduler, time management, productivity tools, Biostrap, Oura, Whoop, Ultrahuman, founder productivity, executive scheduling, peak performance" />
        <link rel="canonical" href="https://chronocal.io/" />
        <meta property="og:title" content="ChronoCal - The Performance Calendar That Works With Your Biology" />
        <meta property="og:description" content="ChronoCal is the world's first performance-based calendar that schedules your work when your brain is wired to win. Integrate your wearables, optimize your energy, and reclaim 15+ hours of peak productivity per week." />
        <meta property="og:url" content="https://chronocal.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://chronocal.io/assets/images/ui/b5ba0db6-c39f-4016-9785-3b5fe0bbfaaa.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ChronoCal - The Performance Calendar That Works With Your Biology" />
        <meta name="twitter:description" content="Schedule your day based on your biological energy peaks, not just your availability. The world's first chronobiology-based scheduler." />
      </Helmet>
      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-scroll {
          animation: scroll 30s linear infinite;
          will-change: transform;
          white-space: nowrap;
        }
      `}</style>
      {/* LLM-Optimized Structured Data */}
      <LLMOptimizedStructuredData url="https://chronocal.io/" />
      
      {/* BFCM Campaign Banner */}
      <BFCMBanner />
      
      {/* Hero Section */}
      <div className="relative min-h-[600px] lg:min-h-[800px] isolate px-4 sm:px-6 pt-1 lg:pt-14 lg:px-8 bg-gradient-to-br from-white/50 via-white/10 to-purple-400/40 overflow-hidden">
        {/* Background decoration */}

        <div className="mx-auto max-w-7xl py-5 sm:py-20 md:py-0 lg:py-10 xl:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center lg:items-start ">
            <div className="text-center lg:text-left col-span-3">
              <h1 className="hidden xl:inline-block text-4xl leading-tight sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
                <span className="mr-1 inline-block">Schedule Like Your&nbsp;</span>
                <TextShimmer
                  as="span"
                  duration={3}
                  spread={20}
                  repeatDelay={2}
                  className="hidden xl:inline-block leading-tight [--base-color:theme(colors.blue.500)] [--base-gradient-color:theme(colors.orange.500)] dark:[--base-color:theme(colors.blue.400)] dark:[--base-gradient-color:theme(colors.blue.100)]"
                >
                 Biology
                </TextShimmer>
                <span>&nbsp; Intended</span>
              </h1>
              <h1 className="inline-block xl:hidden text-4xl lg:text-5xl xl:text-5xl font-bold tracking-tight leading-tight md:leading-tight xl:leading-tight text-foreground mb-6">
              Your Calendar Is <span className="text-blue-700">Destroying</span> Your Best Work
              </h1>              

              <div className="max-w-3xl m-auto lg:m-0 lg:max-w-2xl ">
                <p className="text-lg text-zinc-700 mb-8 leading-relaxed sm:text-xl">
                  High-stakes decisions at 2 PM. Deep work squeezed into tired afternoons. ChronoCal fixes this by syncing with your biology and scheduling meetings when you're sharp and protecting your Power Zones for breakthrough thinking.
                </p>
              </div>              

              <div className="mb-2 flex flex-col sm:flex-row gap-4 justify-center p-2 lg:justify-start">
                <Button 
                  onClick={handleGoogleSignUp}
                  disabled={googleLoading}
                  variant="default"
                  className="font-medium rounded-xl px-6 py-6 text-md  bg-gradient-to-r from-purple-600 to-orange-600 hover:to-orange-500 hover:from-purple-500  text-white transition-all duration-100"
                >
                  <span className="drop-shadow-[0_1px_5px_rgba(0,0,0,0.1)]">
                    <GoogleLogo />
                  </span>
                  {googleLoading ? "Connecting..." : "Sign up with Google"}
                </Button>
                <Button 
                  onClick={() => window.location.href = '/signup'}
                  variant="outline"
                  className="bg-gray-100 hover:border-black/50 font-medium rounded-xl px-6 py-6 text-md group"
                >
                 Sign up with Email <ArrowRight className="ml-2 w-4 h-4 inline-transition-transform group-hover:translate-x-0.5" />
                </Button>                
              </div>
              
              <div className="mb-6 text-center lg:text-left">
                <p className="text-sm text-zinc-600 flex flex-wrap items-center justify-center lg:justify-start gap-x-4 gap-y-1">
                  <span className="flex items-center"><Check className="w-4 h-4 mr-1 text-green-600" /> No credit card required</span>
                  <span className="flex items-center"><Check className="w-4 h-4 mr-1 text-green-600" /> 2-minute setup</span>
                  <span className="flex items-center"><Check className="w-4 h-4 mr-1 text-green-600" /> Free trial</span>
                </p>
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground justify-start sm:justify-center lg:justify-start">
                <div className="flex flex-col sm:flex-col items-start sm:items-center lg:items-start gap-2 sm:gap-4">
                  <p className="text-xs font-semibold text-left text-zinc-400 uppercase mb-2">
                    Supported Wearables
                  </p> 
                  <div className="flex gap-10 sm:gap-2 overflow-hidden">
                    {/* Desktop: Static 4 logos */}
                    <div className="hidden sm:flex items-center gap-6">
                      <BiostrapLogo />
                      <OuraLogo />
                      <WhoopLogo />
                      <UltrahumanLogo />
                    </div>
                    {/* Mobile: Animated horizontal scroll with vertical centering */}
                    <div className="flex sm:hidden overflow-hidden">
                      <div className="animate-scroll flex items-center gap-4">
                        <BiostrapLogo />
                        <OuraLogo />
                        <WhoopLogo />
                        <UltrahumanLogo />
                        <BiostrapLogo />
                        <OuraLogo />
                        <WhoopLogo />
                        <UltrahumanLogo />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Energy Dashboard Preview */}
            <div className="relative col-span-3 lg:col-span-2">
              <div className="lg:max-w-md xl:max-w-lg mx-auto">
                <EnergyDashboardPreview />
              </div>
              
              {/* Wearables Image - eager load for above-the-fold content */}
              <div className="relative -mt-16 sm:-mt-48 lg:-mt-48 flex justify-end">
                <LazyImage
                  src="/assets/images/ui/wearables.webp"
                  alt="Supported wearable devices including Biostrap, Oura, WHOOP, and Ultrahuman"
                  className="w-64 sm:w-96 h-auto border-gray-200 z-20 relative -mr-12 -mb-2 sm:-mr-24 sm:mt-24"
                  sizes="(min-width: 1024px) 24rem, (min-width: 640px) 20rem, 16rem"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Beautiful Booking Page Showcase */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 sm:bg-gradient-none py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Interactive, Energy-Optimized Booking Experience</h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              Watch how your clients experience a beautiful booking interface that automatically suggests optimal meeting times based on your individualized chronobiology and energy levels.
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="relative">
              {/* Mobile: Show image - eager load for above-the-fold content */}
              <LazyImage
                src="/assets/images/ui/29cb9b9a-44dc-40c6-a797-fec0464b4093.png"
                alt="ChronoCal booking page showing beautiful gradient design with preferred times based on circadian rhythm"
                className="w-full rounded-2xl shadow-2xl border border-gray-200 block lg:hidden"
                sizes="100vw"
                loading="eager"
                fetchPriority="high"
              />
              {/* XL+ screens: Show animated demo */}
              <div className="hidden xl:block">
                <BookingFlowDemo />
              </div>
              
              {/* Overlay badges */}
              <div className="hidden absolute top-4 left-4 sm:top-6 sm:left-6">
                <Badge className="bg-green-500 text-white">
                  <Heart className="w-3 h-3 mr-1" />
                  Energy Optimized
                </Badge>
              </div>
              
              <div className="hidden absolute top-4 right-4 sm:top-6 sm:right-6">
                <Badge className="bg-blue-500 text-white">
                  <Brain className="w-3 h-3 mr-1" />
                  Circadian Aware
                </Badge>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <p className="text-muted-foreground">
                <span className="hidden xl:inline">
                  <strong>Interactive Demo:</strong> Watch the animated booking flow in action. 
                </span>
                <span className="hidden md:block xl:hidden">
                  <strong>Video Demo:</strong> See the booking experience in action.
                </span>
                <span className="block md:hidden">
                  <strong>Booking Interface:</strong> Experience the beautiful, energy-optimized booking flow.
                </span>
                {" "}
                <strong>Preferred Times</strong> are automatically suggested based on your unique circadian rhythm, 
                ensuring meetings happen when you're naturally at peak performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="bg-background py-12 sm:py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Join 500+ High-Performers Who've Reclaimed 7,500+ Hours of Peak Productivity</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[{
            name: "Sarah Chen",
            role: "CEO, TechFlow (raised $12M Series A)",
            image: "https://images.unsplash.com/photo-1621390842036-f01b53d9cbfd?w=40&h=40&fit=crop&crop=face&auto=format",
            quote: "I used to schedule investor calls whenever they were available—often at 3 PM when my brain was mush. ChronoCal fixed that. Now my booking page only shows my 9-11 AM Power Zone. I've closed 2 more deals this quarter because I'm sharp when it matters."
          }, {
            name: "Marcus Rodriguez",
            role: "Venture Partner, Summit Ventures",
            image: "https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?w=40&h=40&fit=crop&crop=face&auto=format",
            quote: "As a VC, I'm in back-to-back meetings all day. I thought that was just the job. ChronoCal showed me I was making investment decisions during my worst cognitive hours. Now I protect my morning Power Zone for due diligence, and my decision quality has improved dramatically."
          }, {
            name: "Elena Vasquez",
            role: "Creative Director, Pixel & Co.",
            image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face&auto=format",
            quote: "The wearable integration is genius. My Oura data used to just tell me I slept poorly—now it actually does something about it. ChronoCal automatically lightens my meeting load on low-energy days and protects creative time when I'm at my best. Game-changer for creative work."
          }].map((testimonial, index) => <Card key={index} className="bg-muted/30">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center mb-4">
                    <img src={testimonial.image} alt={testimonial.name} className="w-10 h-10 rounded-full mr-3" />
                    <div>
                      <p className="font-semibold text-sm">{testimonial.name}</p>
                      <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-md text-zinc-700 mb-3">{testimonial.quote}</p>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="bg-background py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Traditional Calendars Are Productivity Killers
              </h2>
              
              <div className="space-y-6">
                {[{
                title: "You're Making Million-Dollar Decisions at 2 PM",
                description: "That's when your cortisol crashes and decision quality plummets by 40%. But your calendar doesn't know that—so it keeps booking high-stakes calls during your biological low points. You're not indecisive. Your calendar is just scheduling you for failure."
              }, {
                title: "Your Best 3 Hours Got Chopped Into 6 Meetings",
                description: "You had a morning Power Zone perfect for deep work—the kind where you solve complex problems and do your best thinking. Instead, it's fragmented by back-to-back calls. By afternoon, when meetings end, your brain is too tired for the work that actually moves the needle."
              }, {
                title: "You're Burning Out Because Your Calendar Ignores Biology",
                description: "Humans aren't machines. Your energy fluctuates by 60% throughout the day. Traditional calendars treat 9 AM and 4 PM as identical—scheduling burnout into your week, one misaligned meeting at a time."
              }].map((problem, index) => <div key={index} className="flex items-start">
                    <div className="bg-destructive rounded-full p-2 mr-4 mt-1 flex-shrink-0">
                      <X className="w-4 h-4 text-destructive-foreground" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{problem.title}</h3>
                      <p className="text-muted-foreground">{problem.description}</p>
                    </div>
                  </div>)}
              </div>
            </div>
            
            <div>
              <Card className="bg-gradient-to-br from-primary to-blue-600 text-white">
                <CardContent className="p-6 sm:p-8">
                  <h3 className="text-2xl font-bold mb-6">ChronoCal Automatically Fixes This</h3>
                  
                  <div className="space-y-5">
                    {[
                      {
                        title: "Your booking page shows only your peak hours",
                        description: "When clients book with you, they only see slots during your Power and Focus Zones. No more 4 PM strategy calls when you're mentally drained."
                      },
                      {
                        title: "AI protects your best hours before meetings fill them",
                        description: "ChronoCal learns your energy patterns and automatically blocks your morning Power Zones for deep work—before random meetings can claim them."
                      },
                      {
                        title: "Real-time energy forecasts from your wearable",
                        description: "Wake up to a personalized energy curve showing exactly when to schedule high-stakes work. Poor sleep last night? ChronoCal automatically suggests lighter meeting loads."
                      },
                      {
                        title: "Intelligent recovery scheduling prevents burnout",
                        description: "After intense work periods, ChronoCal automatically suggests lighter meeting loads based on your HRV and sleep data. It sees burnout coming before you do."
                      }
                    ].map((benefit, index) => <div key={index} className="flex items-start">
                        <div className="bg-green-500 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <div>
                          <span className="text-sm sm:text-base font-semibold block mb-1">{benefit.title}</span>
                          <span className="text-xs sm:text-sm text-white/90">{benefit.description}</span>
                        </div>
                      </div>)}
                  </div>
                  
                  <div className="mt-8 p-4 bg-white/20 rounded-lg">
                    <p className="text-lg font-semibold mb-2">THE OUTCOME:</p>
                    <div className="space-y-1 text-sm">
                      <p>✓ 15+ hours of peak productivity reclaimed per week</p>
                      <p>✓ 40% better decision quality</p>
                      <p>✓ 3x deeper focus sessions</p>
                      <p>✓ 60% less scheduling stress</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Hover Effects */}
      <section className="bg-muted/30 py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Science-Backed Features That Actually Work</h2>
            <p className="text-lg sm:text-xl text-muted-foreground">Built on circadian research and validated with real biometric data</p>
            <div className="mt-6">
              <Link to="/features" className="text-primary hover:underline font-medium inline-flex items-center gap-2">
                Explore All Features <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          
          <FeaturesSectionWithHoverEffects />
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-background py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Get Started in Minutes, See Results in Days</h2>
            <p className="text-lg sm:text-xl text-muted-foreground">Simple setup, powerful results</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/pricing" className="text-primary hover:underline font-medium">View Pricing</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/science" className="text-primary hover:underline font-medium">Learn the Science</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/blog" className="text-primary hover:underline font-medium">Read Our Blog</Link>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[{
            step: "1",
            title: "Connect Your Data",
            description: "Link your wearable device and calendar. ChronoCal analyzes your sleep, recovery, and existing schedule patterns."
          }, {
            step: "2",
            title: "AI Learns Your Rhythms",
            description: "Our algorithm discovers your unique chronotype and energy patterns over 7-14 days of observation."
          }, {
            step: "3",
            title: "Optimize Automatically",
            description: "ChronoCal starts suggesting optimal meeting times and protecting your peak hours for maximum productivity."
          }].map((step, index) => <div key={index} className="text-center">
                <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary-foreground text-2xl font-bold">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>)}
          </div>
          
          <div className="mt-12 sm:mt-16">
            <Card className="max-w-4xl mx-auto shadow-lg">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-2xl font-bold text-center mb-6">The Results Speak for Themselves</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {[{
                  metric: "15+",
                  description: "Hours of peak productivity reclaimed per week"
                }, {
                  metric: "40%",
                  description: "Reduction in decision fatigue"
                }, {
                  metric: "3x",
                  description: "Better deep work session quality"
                }, {
                  metric: "60%",
                  description: "Less scheduling-related stress"
                }].map((stat, index) => <div key={index} className="text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-primary mb-2">{stat.metric}</div>
                      <p className="text-xs sm:text-sm text-muted-foreground">{stat.description}</p>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Founder Quote Section */}
      <section className="bg-background py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-muted/50">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start gap-6">
                  <img 
                    src="/assets/images/avatars/SameerSontakey.jpg" 
                    alt="Sameer Sontakey" 
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex-shrink-0 object-cover"
                  />
                  <div>
                    <blockquote className="text-lg sm:text-xl text-muted-foreground italic mb-4">
                      "After scaling Biostrap and having countless back-to-back meetings, I realized that more meetings per day weren't leading to better productivity. In fact, they were destroying it. I was making important decisions during my natural energy crashes and wondering why everything felt so hard. ChronoCal was born from my own frustration with traditional calendars that ignore our biology. It's the tool I wish I had when building my previous companies."
                    </blockquote>
                    <div>
                      <p className="font-semibold text-lg">Sameer Sontakey</p>
                      <p className="text-muted-foreground">Founder & CEO, ChronoCal</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-muted/30 py-12 sm:py-16 md:py-20" itemScope itemType="https://schema.org/FAQPage">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </div>
          
          <div className="space-y-6">
            {[{
            question: "How is this different from just blocking time in my calendar?",
            answer: "Time blocking assumes your energy is constant. It's not. Your 9 AM and 3 PM are biologically different—your decision quality can drop 40% in afternoon slumps. ChronoCal uses real biometric data from your wearable to identify when you're actually at peak performance, then automatically protects those hours and surfaces them in your booking links. It's dynamic time blocking that adapts to your biology daily, not static blocks that ignore how you actually feel."
          }, {
            question: "Which wearable devices does ChronoCal support?",
            answer: "ChronoCal integrates with Biostrap, Oura Ring, Whoop, and Ultrahuman. We're continuously adding support for new devices based on user demand."
          }, {
            question: "How long does setup actually take?",
            answer: "2 minutes. Connect your Google Calendar (1 click), connect your wearable (1 click), and you're done. ChronoCal starts analyzing your patterns immediately. You'll see your first energy forecast within 24 hours, and the AI refines its predictions over 7-14 days as it learns your unique rhythms. No manual data entry, no complex configuration."
          }, {
            question: "My schedule changes constantly. Will this still work?",
            answer: "Yes. ChronoCal doesn't rigidly block your calendar—it provides intelligent suggestions and surfaces your best available times in booking links. If an urgent meeting needs to happen during a Drift Zone, you can still book it. The system learns from your patterns and adapts. Think of it as a smart assistant that nudges you toward better scheduling, not a rigid system that controls you."
          }, {
            question: "Is my health data secure?",
            answer: "Absolutely. ChronoCal is HIPAA compliant with enterprise-grade encryption. Your biometric data is processed locally when possible and never shared with third parties. Your team only sees your availability and suggested meeting times—never your sleep scores, HRV, or other health metrics."
          }, {
            question: "Can my whole team use this?",
            answer: "Absolutely. Each team member gets their own personalized energy forecast based on their wearable data. When scheduling team meetings, ChronoCal can find overlapping high-energy windows where everyone is in their Power or Focus Zones—so your team meetings are productive, not draining. Team plans start at $12/user/month."
          }, {
            question: "Can I use ChronoCal without a wearable device?",
            answer: "Yes! ChronoCal can learn your patterns through self-reported energy levels and schedule analysis, though wearable integration provides more accurate and automated insights."
          }, {
            question: "Does it work with existing calendar systems?",
            answer: "ChronoCal integrates seamlessly with Google Calendar, Outlook, Apple Calendar, and most major calendar platforms. It works as an intelligent layer on top of your existing setup."
          }].map((faq, index) => <Card key={index} className="shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-36 bg-gradient-to-br from-primary via-primary/90 to-blue-600 text-primary-foreground relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Join 500+ High-Performers Who've Reclaimed Their Best Hours</h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8">Start your 30-day free trial. No credit card required.</p>
          
          <div className="max-w-lg mx-auto">
          <div className="mb-4 flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleGoogleSignUp}
                  disabled={googleLoading}
                  variant="default"
                  className="font-medium rounded-xl px-6 py-6 text-md bg-white text-purple-600 hover:bg-gray-100"
                >
                  <img 
                    src="/assets/images/ui/google-g-logo.svg" 
                    alt="Google" 
                    className="w-6 h-6 mr-3"
                  />
                  {googleLoading ? "Connecting..." : "Sign up with Google"}
                </Button>
                <Button 
                  onClick={() => window.location.href = '/signup'}
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-purple-600 font-medium rounded-xl px-6 py-6 text-md"
                >
                 Sign up with Email <ArrowRight className="ml-2 w-4 h-4 inline transition-transform group-hover:translate-x-0.5" />
                </Button>                
              </div>
              
              <p className="text-sm text-white/80 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                <span className="flex items-center"><Check className="w-4 h-4 mr-1" /> Connect wearable in 1 click</span>
                <span className="flex items-center"><Check className="w-4 h-4 mr-1" /> See energy forecast in 24 hours</span>
                <span className="flex items-center"><Check className="w-4 h-4 mr-1" /> Cancel anytime</span>
              </p>
          </div>
          
          <p className="text-white/75 mt-8 text-sm">127 high-performers joined this week. See why.</p>
        </div>
      </section>

      <Footer />
      
      {/* LLM Content Extraction Helper */}
      <LLMContentExtraction />
    </div>;
};
export default Index;
